import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { ApiService } from '@dav-core/services/api.service';
import { FilterService } from '@dav-core/services/filter.service';
import { ProductService } from '@dav-core/services/product.service';
import { SortingService } from '@dav-core/services/sorting.service';
import { CartValidationService } from '@dav-core/validations/cart/cart.validation';
import { UserResponse } from '@dav-model/login/response/user.response.model';
import { MutedProductCodes } from '@dav-model/product-codes';
import { GetProductsRequest } from '@dav-model/request/product.request';
import { GetProductsResponse, ProductItem } from '@dav-model/response/getproducts.response';
import * as fromRoot from '@dav-store/reducers';
import * as UserReducer from '@dav-store/user/user.reducer';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';

import * as ProductActions from './product.actions';
import * as CartActions from '../cart/cart.actions';

const _filterOutMutedProductsFromResponse = (response: GetProductsResponse): GetProductsResponse => {
  return {
    ...response,
    availableOptions: {
      ...response.availableOptions,
      products: {
        ...(response.availableOptions?.products ?? null),
        productItem:
          response.availableOptions?.products?.productItem.filter(
            (product) => !MutedProductCodes.includes(product.productCode)
          ) ?? [],
      },
    },
  };
};

@Injectable()
export class ProductEffects {
  constructor(
    private actions$: Actions,
    private apiService: ApiService,
    private store$: Store<fromRoot.State>,
    private filterService: FilterService,
    private cartValidationService: CartValidationService
  ) {}

  getProducts$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActions.GET_PRODUCTS),
      withLatestFrom(this.store$.select((state) => state.userState)),
      mergeMap(([action, userState]: [ProductActions.GetProducts, UserReducer.State]) =>
        this.getProductsByClient(action.payload, userState.user).pipe(
          map((response) => SortingService.sortProductResponse(response)),
          map((response) => ProductService.prepProductsForSurcharges(action.payload, response)),
          map((response) => {
            this.store$.dispatch(new CartActions.ResetCartAction());
            const filters = this.filterService.fetchFilters(response.availableOptions.products.productItem);
            const rawProducts = response.availableOptions.products.productItem;
            let gridProducts = ProductService.productResponseDataMapper(rawProducts);
            if (gridProducts && gridProducts.length) {
              gridProducts = gridProducts.sort((a, b) =>
                a.productDescription.enUsProductDescription > b.productDescription.enUsProductDescription ? 1 : -1
              );
            }

            return {
              type: ProductActions.GET_PRODUCTS_SUCCESS,
              payload: {
                response: response,
                gridProducts: gridProducts,
                filters: filters,
                markup: userState.markup,
              },
            };
          }),
          // If request fails, dispatch failed action
          catchError((error: HttpErrorResponse) => {
            return of({
              type: ProductActions.GET_PRODUCTS_FAILURE,
              payload: { error: error.statusText },
            });
          })
        )
      )
    )
  );

  filterGrid$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActions.FILTER_GRID),
      withLatestFrom(this.store$.select((state) => state.productState.products)),
      switchMap(([action, products]: [ProductActions.FilterGridAction, GetProductsResponse]) => {
        let allProducts = products.availableOptions.products.productItem;
        if (allProducts && allProducts.length) {
          allProducts = [...allProducts].sort((a, b) =>
            a.productDescription.enUsProductDescription > b.productDescription.enUsProductDescription ? 1 : -1
          );
        }
        const filteredProducts = this.filterService.filter(action.payload, allProducts);

        return of({
          type: ProductActions.FILTER_GRID_SUCCESS,
          payload: { filters: action.payload, grid: filteredProducts },
        });
      })
    )
  );

  applySurcharges$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ProductActions.APPLY_SURCHARGES),
        withLatestFrom(this.store$.select((state) => state.productState.grid)),
        map(([, products]) => {
          products?.forEach((product) =>
            this.cartValidationService.validateGridCostByRetailType(product.customerCost, product)
          );
        })
      ),
    { dispatch: false }
  );

  // Get Products based on client whether its CS 3.1 or Aggregator
  private getProductsByClient(request: GetProductsRequest, user: UserResponse) {
    const updatedRequest = { ...request, programId: user.programId };

    // call aggregator service if thre are related dealers
    if (user.isRelatedDealer) {
      // set variable to request data for aggregator service
      const req = ProductService.aggregateProductRequestMapper(updatedRequest);

      return this.apiService.getProductsFromAggregator(req).pipe(
        map((response) => _filterOutMutedProductsFromResponse(response)),
        switchMap((resp: GetProductsResponse) => {
          const response = ProductService.assignPropertiesToProducts(resp, user);

          return of(response);
        })
      );
    } else {
      return this.apiService
        .getProducts(request)
        .pipe(map((response) => _filterOutMutedProductsFromResponse(response)));
    }
  }
}
